import React, { useState } from 'react';
import { BreadcrumbProps, Button, Icon, NumberDisplay, Tooltip } from '@aidkitorg/component-library';
import { useParams } from 'react-router-dom';
import { usePayments } from '../../Contexts/PaymentsContext';
import { useLocalizedStrings } from '../../Localization';
import { AdminPanelPage } from '../AdminPanelPage';
import { PaymentBatchDownloadModal } from './components/PaymentBatchDownloadModal';
import { PaymentBatchRecipientsDataTable } from './components/PaymentBatchRecipientsDataTable';
import { usePaymentDetailsConfig } from '../shared/hooks/useAdminPanelPageConfig';

export const PaymentBatchDetailsPage = () => {
  const { batch_name } = useParams<{ batch_name: string }>()
  const [downloadModalOpen, setDownloadModalOpen] = useState<boolean>(false)
  const L = useLocalizedStrings()
  const {
    fetchRecipientDetailsByBatch,
    getBatchTimestamp,
    getBatchNumRecipients,
    getBatchName,
    getBatchNumFailedPayments,

    getIsBatchLoading,
  } = usePayments();

  const paymentBatchTimestamp = getBatchTimestamp(batch_name)
  const paymentBatchNumRecipients = getBatchNumRecipients(batch_name)
  const paymentBatchName = getBatchName(batch_name)
  const paymentBatchNumFailedPayments = getBatchNumFailedPayments(batch_name)

  const isPaymentBatchLoading = getIsBatchLoading(batch_name)

  // Fetch page configuration using the hook
  const {
    pageConfig,
    isLoading: isConfigLoading,
    getLocalizedText,
    isVisible
  } = usePaymentDetailsConfig();

  // Use config to determine what to show
  const showTotalRecipients = isVisible(['summaryMetrics', 'totalRecipients']);
  const showFailedPayments = isVisible(['summaryMetrics', 'failedPayments']);
  const showExportButton = isVisible(['exportButton']);
  const pageDescription = getLocalizedText(['pageHeader', 'description'], L.admin_panel.payments.payment_details_desc);

  const [isTableExpanded, setIsTableExpanded] = useState(false)

  const totalRecipientsCard = showTotalRecipients ? (<NumberDisplay
    title={L.admin_panel.payments.total_recipients}
    value={paymentBatchNumRecipients}
    iconName="UsersIcon"
    iconColor='blue'
    loading={isPaymentBatchLoading}
  />
  ) : null;

  const numFailedPaymentsCard = showFailedPayments ? (<NumberDisplay
    title={L.admin_panel.payments.failed_payments}
    value={paymentBatchNumFailedPayments}
    iconName="XCircleIcon"
    iconColor='red'
    loading={isPaymentBatchLoading}
  />
  ) : null;

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.payments,
      href: '/admin-panel/payments'
    },
    ...((isPaymentBatchLoading || !paymentBatchName) ? [] : [{
      label: `${paymentBatchName}`,
    }])
  ];
  
  const toggleButton = <Button
    size="sm"
    variant="secondary"
    onClick={() => setIsTableExpanded(prev => (!prev))}
    icon={<Icon
      role="decorative"
      type="solid"
      name={isTableExpanded ? "ArrowsPointingInIcon" : "ArrowsPointingOutIcon"}
    />}
    title={isTableExpanded ? L.admin_panel.reports.collapse_table_view : L.admin_panel.reports.expand_table_view}
    aria-label={isTableExpanded ? L.admin_panel.reports.collapse_table_view : L.admin_panel.reports.expand_table_view}
  />

  const pageActionButtons = [
    <Tooltip text={L.admin_panel.payments.refresh_data} asChild key="refresh">
      <Button
        size="sm"
        variant='secondary'
        onClick={async () => {
          await fetchRecipientDetailsByBatch(batch_name, true)
        }}
        icon={<Icon role="decorative" type="solid" name="ArrowPathIcon" />}
        aria-label={L.admin_panel.payments.refresh_data}
      />
    </Tooltip>
  ];

  if (showExportButton) {
    pageActionButtons.push(
      <Tooltip text={L.admin_panel.payments.export_recipients} asChild key="export">
        <Button size="sm" variant="secondary" onClick={() => setDownloadModalOpen(true)} disabled={isPaymentBatchLoading}>
          <div className="flex gap-2 items-center">
            <Icon name="ArrowDownTrayIcon" role="decorative" type="solid" />
          </div>
        </Button>
      </Tooltip>
    );
  }

  return (
    <AdminPanelPage
      fullWidth={isTableExpanded}
      breadcrumbs={breadcrumbs}
      title={`${paymentBatchName}`}
      description={!isPaymentBatchLoading && pageDescription}
      pageActionButtons={pageActionButtons}
    >
      <div className="w-full h-full">
        <div className="flex flex-col gap-4">
          {(totalRecipientsCard || numFailedPaymentsCard) && (
            <div className="flex flex-col lg:flex-row gap-2">
              {totalRecipientsCard && (
                <div className="w-full lg:w-auto">
                  {totalRecipientsCard}
                </div>
              )}
              {numFailedPaymentsCard && (
                <div className="w-full lg:w-auto">
                  {numFailedPaymentsCard}
                </div>
              )}
            </div>
          )}
          <PaymentBatchRecipientsDataTable
            toggleButton={toggleButton}
            lastUpdatedTimestamp={paymentBatchTimestamp}
            pageConfig={pageConfig}
            isConfigLoading={isConfigLoading}
          />
          <PaymentBatchDownloadModal batchName={batch_name} downloadModalOpen={downloadModalOpen} setDownloadModalOpen={setDownloadModalOpen} />
        </div>
      </div>
    </AdminPanelPage>
  )
}